/* Product page behaviour: gallery, variant picker, add to cart and the mobile sticky order bar.
   Variant changes re-render price, stock and the WhatsApp button through the Section Rendering API,
   then announce themselves with the variant:change event the WhatsApp button already listens to.
   Wrapped in a block so nothing here leaks into the global scope shared with macsolve.js. */
{
  /* Gallery: scroll-snap viewer, thumbnails follow it, pointer zoom from 990px */
  class MsGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      if (!this.track) return;
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.select(thumb.dataset.thumb)));
      this.track.addEventListener('scroll', () => this.queue(), { passive: true });
      this.track.addEventListener('pointermove', (event) => this.zoom(event));
      if (this.dataset.start) this.select(this.dataset.start, false);
    }

    select(id, smooth = true) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (index < 0) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      this.mark(index);
    }

    queue() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.mark(Math.round(this.track.scrollLeft / this.track.clientWidth));
      });
    }

    mark(index) {
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    zoom(event) {
      if (event.pointerType !== 'mouse') return;
      const slide = event.target.closest('[data-media-id]');
      if (!slide) return;
      const box = slide.getBoundingClientRect();
      slide.style.setProperty('--ms-zx', `${((event.clientX - box.left) / box.width) * 100}%`);
      slide.style.setProperty('--ms-zy', `${((event.clientY - box.top) / box.height) * 100}%`);
    }
  }
  customElements.define('ms-gallery', MsGallery);

  /* Variant picker, add to cart and sticky bar */
  class MsProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form.ms-pdp__form');
      this.options = this.querySelector('[data-options]');
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.idInput = this.form?.querySelector('input[name="id"]');
      if (!this.idInput && this.form && this.variants.length) {
        this.idInput = document.createElement('input');
        this.idInput.type = 'hidden';
        this.idInput.name = 'id';
        this.idInput.value = this.currentVariant()?.id ?? this.variants[0].id;
        this.form.prepend(this.idInput);
      }
      this.options?.addEventListener('change', () => this.onOptionChange());
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
      this.markAvailability();
      this.initSticky();
      if (typeof msTrack === 'function') {
        msTrack('view_item', { product_id: this.dataset.productId, currency: this.dataset.currency });
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    chosenOptions() {
      return [...(this.options?.querySelectorAll('fieldset') ?? [])].map((set) => set.querySelector('input:checked')?.value);
    }

    currentVariant() {
      const chosen = this.chosenOptions();
      return this.variants.find((variant) => variant.options.every((value, i) => value === chosen[i]));
    }

    /* Mark option values that no available variant can satisfy, given the other chosen options */
    markAvailability() {
      const chosen = this.chosenOptions();
      this.options?.querySelectorAll('fieldset').forEach((set, position) => {
        set.querySelectorAll('input').forEach((input) => {
          const reachable = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[position] === input.value &&
              variant.options.every((value, i) => i === position || value === chosen[i])
          );
          input.closest('label').classList.toggle('ms-option__pill--sold', !reachable);
        });
      });
    }

    async onOptionChange() {
      this.markAvailability();
      const variant = this.currentVariant();
      this.setError('');
      if (!variant) {
        this.setAvailability(false);
        this.setError(this.dataset.labelMissing);
        return;
      }
      this.idInput.value = variant.id;
      this.setAvailability(variant.available);
      window.history.replaceState(null, '', `${window.location.pathname}?variant=${variant.id}`);
      await this.refresh(variant);
    }

    setAvailability(available) {
      const add = this.querySelector('[data-add]');
      if (add) add.hidden = !available;
    }

    setError(message) {
      const target = this.querySelector('[data-error]');
      if (!target) return;
      target.textContent = message || '';
      target.hidden = !message;
    }

    async refresh(variant) {
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const url = `${this.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.sectionId}`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const next = new DOMParser().parseFromString(await response.text(), 'text/html');
        next.querySelectorAll('[data-swap]').forEach((fresh) => {
          const current = this.querySelector(`[data-swap="${fresh.dataset.swap}"]`);
          if (current) current.innerHTML = fresh.innerHTML;
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
      this.querySelector('ms-gallery')?.select(variant.featured_media?.id ?? '');
      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { id: variant.id, title: variant.title, price: this.priceText(), available: variant.available },
        })
      );
    }

    /* The formatted price as the server rendered it, without the screen-reader-only labels */
    priceText() {
      const current = this.querySelector('[data-swap="price"] .ms-price__current');
      if (!current) return '';
      const clone = current.cloneNode(true);
      clone.querySelectorAll('.ms-visually-hidden').forEach((node) => node.remove());
      return clone.textContent.trim();
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[data-add]');
      const label = button.querySelector('[data-add-label]');
      const idle = label.textContent;
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.setAttribute('aria-disabled', 'true');
      label.textContent = this.dataset.labelAdding;
      this.setError('');
      try {
        const root = window.Shopify?.routes?.root ?? '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form),
        });
        const result = await response.json();
        if (!response.ok) {
          this.setError(result.description || result.message || this.dataset.labelError);
          return;
        }
        if (typeof msTrack === 'function') {
          msTrack('add_to_cart', {
            product_id: this.dataset.productId,
            currency: this.dataset.currency,
            value: (result.final_line_price ?? result.final_price) / 100,
          });
        }
        /* The cart drawer claims the event with preventDefault; otherwise open the cart page */
        const handled = !document.dispatchEvent(new CustomEvent('cart:add', { cancelable: true, detail: { item: result } }));
        if (!handled) window.location.href = this.dataset.cartUrl;
      } catch (error) {
        this.setError(this.dataset.labelError);
      } finally {
        button.removeAttribute('aria-disabled');
        label.textContent = idle;
      }
    }

    /* Phones: show the order bar once the main buttons have scrolled out above the viewport */
    initSticky() {
      const bar = this.querySelector('[data-sticky]');
      const anchor = this.querySelector('[data-cta-anchor]');
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.hidden = !past;
        /* The bar only exists on phones; on desktop the floating WhatsApp button must stay (989px = below the desktop breakpoint) */
        document.body.classList.toggle('ms-has-sticky', past && window.matchMedia('(max-width: 989px)').matches);
      });
      this.observer.observe(anchor);
    }
  }
  customElements.define('ms-product-form', MsProductForm);

  /* Related products: Shopify only fills recommendations when the section is fetched from its own endpoint */
  class MsRecommendations extends HTMLElement {
    async connectedCallback() {
      if (!this.dataset.url) return;
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;
        const next = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = next.querySelector('.ms-related');
        if (fresh) this.replaceWith(fresh);
      } catch (error) {
        /* The row is a nicety; stay empty if it cannot load */
      }
    }
  }
  customElements.define('ms-recommendations', MsRecommendations);
}
